export const RESOURCE_TYPES = {
  SOURCE: 'source',
  DESTINATION: 'destination',
  TRANSFORMATION: 'transformation',
} as const;

export const DIRECTORY_TABS = {
  ALL_SOURCES: 'All sources',
  EVENT_STREAMS: 'EventStream sources',
  CLOUD_EXTRACT: 'Cloud Extract sources',
  WAREHOUSE_ACTIONS: 'Reverse ETL sources',
  POPULAR_SOURCE: 'Popular sources',
  ALL_DESTINATIONS: 'All destinations',
  WAREHOUSE_DESTINATIONS: 'Warehouse destinations',
  CLOUD_DESTINATIONS: 'Cloud destinations',
  POPULAR_DESTINATION: 'Popular destinations',
};

export const RUDDER_SCOPES = {
  delivery: 'delivery',
  delete: 'delete',
} as const;

export const DEFAULT_INTEGRATION_LEGACY_NOTICE = {
  source: 'This source is considered legacy and will be deprecated in the future.',
  destination: 'This destination is considered legacy and will be deprecated in the future.',
} as const;

export const SOURCE_DEFINITION_CATEGORY = {
  CLOUD: 'cloud',
  SINGER: 'singer-protocol',
  WAREHOUSE: 'warehouse',
  WEBHOOK: 'webhook',
} as const;

export const WEBHOOK_CATEGORY_SOURCE_DEFINITIONS_WITH_CONFIGURE_STEP = [
  'Shopify',
  'Braze',
  'webhook',
];

export const DESTINATION_DEFINITION_CATEGORY = {
  WAREHOUSE: 'warehouse',
} as const;

export const RESOURCE_NAME_MIN_LENGTH = 2;
export const RESOURCE_NAME_MAX_LENGTH = 100;

export const RESOURCE_DESCRIPTION_MIN_LENGTH = 2;
export const RESOURCE_DESCRIPTION_MAX_LENGTH = 200;

// Ref: src/components/dataCatalog/constants.ts
export const TRACKING_PLAN_NAME_MIN_LENGTH = 2;
export const TRACKING_PLAN_NAME_MAX_LENGTH = 65;

export const TRACKING_PLAN_DESCRIPTION_MIN_LENGTH = 3;
export const TRACKING_PLAN_DESCRIPTION_MAX_LENGTH = 2000;

const EmptyRegex = '^$';
const NameRegex = `^[A-Za-z-_. 0-9]{${RESOURCE_NAME_MIN_LENGTH},${RESOURCE_NAME_MAX_LENGTH}}$`;
const ValidResourceIdentifierRegex = '^[A-Za-z-_. 0-9]*$';
const ValidResourceDescriptionRegex = '^[A-Za-z-_.;,/()\\s0-9]*$';
const DescriptionRegex = `^[A-Za-z-_.;,/() 0-9]{${RESOURCE_DESCRIPTION_MIN_LENGTH},${RESOURCE_DESCRIPTION_MAX_LENGTH}}$`;

export const REGEX_STRING = {
  VALID_RESOURCE_IDENTIFIER: ValidResourceIdentifierRegex,
  VALID_RESOURCE_DESCRIPTION: ValidResourceDescriptionRegex,
  SOURCE_NAME: NameRegex,
  ORIGIN_NAME: NameRegex,
  ORIGIN_DESCRIPTION: DescriptionRegex,
  RETL_SOURCE_TYPE_DESCRIPTION: DescriptionRegex,
  DESTINATION_NAME: NameRegex,
  TRANSFORMATION_NAME: NameRegex,
  TRANSFORMATION_DESCRIPTION: `${EmptyRegex}|${DescriptionRegex}`,
  LIBRARY_NAME: NameRegex,
  LIBRARY_DESCRIPTION: `${EmptyRegex}|${DescriptionRegex}`,
  EMAIL: '^[^\\s@]+@[^\\s@]+.[^\\s@]+$',
  TOKEN_NAME: '^[0-9a-zA-Z_ -]{1,64}$',
  ORGANIZATION_NAME: '^[A-Za-z-_. 0-9]{2,50}$',
  WORKSPACE_NAME: '^[A-Za-z-_.+ 0-9]{2,50}$',
  USER_NAME: "^[0-9A-Za-z ,.'-]{1,50}$",
  WHT_PROJECT_NAME: NameRegex,
  WHT_PROJECT_DESCRIPTION: `${EmptyRegex}|${DescriptionRegex}`,
  GIT_REPO_URL: '^git@([\\w.-]+):[\\w-]+/.+$',
  SSH_REPO_URL: /^ssh:\/\/git@([\w.-]+)?:?\d*\/.+$/,
  GIT_REPO_SSH_KEY: '^-----BEGIN OPENSSH PRIVATE KEY-----(.|\n)*-----END OPENSSH PRIVATE KEY-----$',
  WHT_WAREHOUSE_FIELDS: '^.+$',
  TRACKING_PLAN_NAME: /^[A-Z_a-z][\s\w,.-]*$/,
  TRACKING_PLAN_DESCRIPTION: /^[A-Z_a-z].*$/,
  PROFILES_CONCURRENCY: '^([1-9]\\d?|100)$',
  CREDENTIAL_KEY_NAME: '^(?!\\d)\\w+$',
  SERVICE_ACCESS_TOKEN_NAME: NameRegex,
  GROUP_DESCRIPTION: `${EmptyRegex}|^[A-Za-z-_.;,/() 0-9]*$`,
};

export const REGEX_ERROR_MESSAGE = {
  NAME_REGEX:
    'Name must be between 2 and 50 characters long, start with a letter and contain only letters, numbers, underscores, spaces, dashes, and dots.',
  DESCRIPTION_REGEX:
    'Description must be between 2 and 200 characters long, start with a letter and contain only letters, numbers, underscores, commas, spaces, dashes, semicolon, forward slash, parenthesis and dots.',
};

export const URLS = {
  TERMS_OF_SERVICE: 'https://rudderstack.com/master-service-agreement',
  DATA_RETENTION_EVENTS: 'https://www.rudderstack.com/docs/dashboard-guides/data-retention',
  DATA_RETENTION_REPORTING:
    'https://www.rudderstack.com/docs/dashboard-guides/data-retention/#sample-event-data',
  PRIVACY_POLICY: 'https://rudderstack.com/privacy-policy',
  PRICING: 'https://rudderstack.com/pricing',
  DOCS: 'https://docs.rudderstack.com',
  ADD_TRANSFORMATION: 'https://rudderstack.com/docs/transformations/#adding-a-transformation',
  ACCESS_TOKEN: 'https://rudderstack.com/docs/transformations/api-access-token/',
  API: 'https://docs.rudderstack.com/rudderstack-api',
  SAMPLE_TRANSFORMATIONS: 'https://github.com/rudderlabs/sample-user-transformers',
  SLACK_INVITE:
    'https://join.slack.com/t/rudderstack/shared_invite/zt-3tbuc7tkg-KZlCimMjkbirJMtqrcR1ew',
  VIDEO_LIBRARY: 'https://www.youtube.com/channel/UCgV-B77bV_-LOmKYHw8jvBw',
  TRACKING_PLAN_DOCS: 'https://www.rudderstack.com/docs/features/data-governance/tracking-plans/',
  TRACKING_PLAN_MIGRATION:
    'https://www.rudderstack.com/docs/data-governance/tracking-plans/#migrate-tracking-plans',
  RUDDERSTACK_OVERVIEW: 'https://www.youtube.com/watch?v=vOaw9pKn6_M',
  CUSTOMER_METRICS:
    'https://www.rudderstack.com/blog/a-guide-to-customer-metrics-what-to-track-and-why',
  WHT_PROJECT_SETUP: 'https://rudderlabs.github.io/pywht/',
  DESTINATION_DOCS: 'https://www.rudderstack.com/docs/dashboard-guides/destinations/',
  SOURCE_DOCS: 'https://www.rudderstack.com/docs/sources/',
  DESTINATION_OVERVIEW_DOCS: 'https://www.rudderstack.com/docs/destinations/overview/',
  CONNECTIONS_DOCS: 'https://www.rudderstack.com/docs/destinations/rudderstack-connection-modes/',
  LIVE_EVENTS_DOCS: 'https://www.rudderstack.com/docs/dashboard-guides/live-events/',
  DATA_RESIDENCY_DOCS: 'https://www.rudderstack.com/docs/data-residency/',
  UPDATES: 'https://www.rudderstack.com/docs/releases/',
  TRANSFORMATION_TEMPLATE: 'https://www.rudderstack.com/docs/features/transformations/templates/',
  TEAMS: 'https://www.rudderstack.com/docs/dashboard-guides/user-management/',
  USE_TRANSFORMATIONS: 'https://www.rudderstack.com/docs/transformations/usage/',
  CLOUD_MODE_TRANSFORMATIONS: 'https://www.rudderstack.com/docs/transformations/usage/#cloud-mode',
  DEVICE_MODE_TRANSFORMATIONS:
    'https://www.rudderstack.com/docs/transformations/usage/#device-mode',
  QUICKSTART_GUIDE: 'https://www.rudderstack.com/docs/get-started/quickstart/',
  BLOG: 'https://www.rudderstack.com/blog/',
  DATA_GOVERNANCE_AUDIT: 'https://www.rudderstack.com/docs/api/event-audit-api/',
  GEOLOCATION_ENRICHMENT: 'https://www.rudderstack.com/docs/features/geolocation-service',
  CUSTOM_ALERTS: 'https://www.rudderstack.com/docs/data-governance/alerts/#custom-alerts',
  CREDENTIAL_STORE: 'https://rudderstack.com/docs/transformations/credentials/',
  SERVICE_ACCESS_TOKEN: 'https://www.rudderstack.com/docs/dashboard-guides/service-access-tokens/',
  AUDIT_LOGS_API: 'https://www.rudderstack.com/docs/api/audit-logs-api/',
  SSO_SCIM: 'https://www.rudderstack.com/docs/user-guides/administrators-guide/sso-setup/',
  PERSONAL_ACCESS_TOKEN: 'https://www.rudderstack.com/docs/dashboard-guides/personal-access-token/',
  AUDIENCE_COHORT_V2: 'https://www.rudderstack.com/docs/profiles/concepts/audiences/',
  CODEGEN_DOCS: 'https://www.rudderstack.com/docs/data-governance/tracking-plans/ruddertyper',
  CODEGEN_SETUP_DOCS:
    'https://www.rudderstack.com/docs/data-governance/tracking-plans/ruddertyper/#get-started',
  ACCESS_MANAGEMENT_DOCS: 'https://www.rudderstack.com/docs/access-management/overview/',
  ACCESS_MANAGEMENT_MIGRATION_DOCS:
    'https://www.rudderstack.com/docs/access-management/pre-migration-considerations/',
  CLOUD_VS_OPEN_SOURCE:
    'https://www.rudderstack.com/docs/get-started/rudderstack-open-source/cloud-vs-open-source/',
  EVENT_LIMIT_OVERAGE_DOCS:
    'https://www.rudderstack.com/docs/dashboard-guides/billings-plans/#event-limit-overages',
  ETL_SUNSET_RELEASE_NOTES: 'https://www.rudderstack.com/docs/releases/etl-sunset/',
};

export const SESSION_STORAGE_KEYS = {
  source: 'OauthMetadata',
  destination: 'DestOauthMetadata',
};

export const VALIDATION_ERRORS = {
  INVALID_NAME: (entity: string) => `Please enter a valid ${entity} name`,
  NON_UNIQUE_NAME: (entity: string) => `Please enter a unique ${entity} name`,
  INVALID_DESCRIPTION: (entity: string) => `Please enter a valid ${entity} description`,
};

export enum Region {
  US = 'US',
  EU = 'EU',
  IN = 'IN',
}

export const REGIONS_INFO = {
  [Region.US]: {
    name: 'United States',
    shortName: 'US',
    svg: 'us-flag',
  },
  [Region.EU]: {
    name: 'Europe',
    shortName: 'EU',
    svg: 'eu-flag',
  },
  [Region.IN]: {
    name: 'India',
    shortName: 'IN',
    svg: 'in-flag',
  },
} as const;

export const SUPPORT_EMAIL = 'support@rudderstack.com';

export const OBJECT_STORAGE_DEST_DEFINITION_NAMES = ['S3', 'GCS', 'AZURE_BLOB', 'MINIO'];

export const LATENCY_TOOLTIP_MESSAGES = {
  CLOUD_DESTINATION:
    '95% of the data is delivered within this time, measured from ingestion to successful delivery',
  WAREHOUSE_DESTINATION:
    'Maximum time taken to sync data to the destination during the selected time period',
  NO_DATA:
    'Latency data is not available for this destination due to zero event delivery in selected time period',
  WH_LATENCY_NOT_AVAILABLE: 'Latency data is not available for warehouse destinations.',
};

export const REPORTING_LATENCY_TOOLTIP =
  'Metrics may be delayed and not reflect real-time events due to reporting latency';
